import { theme } from '@Guidelines/Theme/theme';
import type UserView from '@UI/Store/Users/Views/UserView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Tag } from '@verclam/ui-kit';

/**
 * Owns its own layout (flex column, right-aligned): the card header pushes this component to its
 * right edge and lets it wrap the MFA badge under the tag on its own, rather than relying on a
 * wrapping `div` supplied by the caller.
 */
const containerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.25rem;
`;

const mfaBadgeStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly user: UserView;
};

/**
 * `status.value` (never re-derived) drives the tint, `status.label` (server-authored French) is the
 * text shown. `invited` escalates from `info` to `warning` once the invitation is expired: the server
 * never sends a distinct `status.value` for that case, so the front compares
 * `invitationExpiresAt` to `Date.now()` itself, via `UserView.isInvitationExpired`.
 */
const UserStatusTag: React.FC<Props> = ({ user }) => {
	const severity = React.useMemo(() => {
		if (user.status.value === 'active') {
			return 'success' as const;
		}

		if (user.status.value === 'deactivated') {
			return 'secondary' as const;
		}

		return user.isInvitationExpired ? ('warning' as const) : ('info' as const);
	}, [user.status.value, user.isInvitationExpired]);

	// Only meaningful once a session exists: a pending invitation has never had the chance to enroll.
	// `password_and_second_factor` is the only mode that requires a credential — `sso` carries its own
	// second factor, `password_only` never asks for one. Mirrors `isExemptFromSecondFactor` on the
	// server as a literal: the rule is not shared, no type crosses the two applications.
	const showsMfaBadge =
		user.status.value === 'active' &&
		user.authenticationMode.value === 'password_and_second_factor' &&
		!user.hasMfaEnabled;

	return (
		<div css={containerStyle}>
			<Tag value={user.status.label} severity={severity} rounded />
			{showsMfaBadge && <span css={mfaBadgeStyle}>2FA non activée</span>}
		</div>
	);
};

export default UserStatusTag;
